<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { registerTutorialIdAlias } from '@/feature/tutorialAgent/tutorialIdAliasStore'

const props = defineProps<{
  root: HTMLElement | null
  scopeId?: string
}>()
const route = useRoute()

const autoTagSelector =
  'button, a, input, textarea, select, [role], [contenteditable="true"], [data-tutorial-id]'
const skipTagNames = new Set(['svg', 'path', 'g', 'span', 'i'])
const taggedElements = new WeakSet<HTMLElement>()
const autoIdHistory = new WeakMap<HTMLElement, string>()
let observer: MutationObserver | null = null
let pending = false
const pendingElements: HTMLElement[] = []

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)

const getAutoLabel = (element: HTMLElement) => {
  const tooltipTextFromDataset = element.dataset.tooltipText
  if (tooltipTextFromDataset && tooltipTextFromDataset.trim().length > 0) {
    return tooltipTextFromDataset.trim().slice(0, 120)
  }
  const describedBy = element.getAttribute('aria-describedby')
  if (describedBy) {
    const tooltipEl = document.getElementById(describedBy)
    const tooltipTextFromReference = tooltipEl?.textContent?.trim()
    if (tooltipTextFromReference) return tooltipTextFromReference.slice(0, 120)
  }
  const ariaLabel = element.getAttribute('aria-label')
  if (ariaLabel && ariaLabel.trim().length > 0) return ariaLabel.trim()
  const title = element.getAttribute('title')
  if (title && title.trim().length > 0) return title.trim()
  const placeholder = element.getAttribute('placeholder')
  if (placeholder && placeholder.trim().length > 0) return placeholder.trim()
  const alt = element.getAttribute('alt')
  if (alt && alt.trim().length > 0) return alt.trim()
  const text = element.textContent?.trim() ?? ''
  if (text.length > 0) return text.slice(0, 120)
  return element.tagName.toLowerCase()
}

const shouldSkipAutoTag = (element: HTMLElement) => {
  const tag = element.tagName.toLowerCase()
  if (tag === 'script' || tag === 'style' || skipTagNames.has(tag)) return true
  if (element.dataset.tutorialIgnore === 'true') return true
  const ignoredAncestor = element.closest('[data-tutorial-ignore="true"]')
  return Boolean(ignoredAncestor)
}

const getScopePrefix = (root: HTMLElement) => {
  if (props.scopeId) return `scope-${slugify(props.scopeId)}`
  const rootId = root.getAttribute('id')
  if (rootId) return `scope-${slugify(rootId)}`
  const dataScope = root.getAttribute('data-tutorial-scope')
  if (dataScope) return `scope-${slugify(dataScope)}`
  return 'scope-main'
}

const getRouteKey = () => {
  if (route.name) return `name-${String(route.name)}`
  if (route.path) return `path-${route.path}`
  return 'route-unknown'
}

const getRoleKey = (element: HTMLElement) => {
  return (
    element.dataset.tutorialRole ?? element.getAttribute('role') ?? element.tagName.toLowerCase()
  )
}

const getSemanticKey = (element: HTMLElement) => {
  const tutorialKey = element.dataset.tutorialKey
  if (tutorialKey && tutorialKey.trim().length > 0) return tutorialKey.trim()
  const tooltipKey = element.dataset.tooltipKey
  if (tooltipKey && tooltipKey.trim().length > 0) return tooltipKey.trim()
  const testId = element.getAttribute('data-testid')
  if (testId && testId.trim().length > 0) return testId.trim()
  const name = element.getAttribute('name')
  if (name && name.trim().length > 0) return name.trim()
  const ariaLabel = element.getAttribute('aria-label')
  if (ariaLabel && ariaLabel.trim().length > 0) return ariaLabel.trim()
  const title = element.getAttribute('title')
  if (title && title.trim().length > 0) return title.trim()
  const placeholder = element.getAttribute('placeholder')
  if (placeholder && placeholder.trim().length > 0) return placeholder.trim()
  const id = element.getAttribute('id')
  if (id && id.trim().length > 0) return id.trim()
  return getAutoLabel(element)
}

const resolveTagTarget = (element: HTMLElement, root: HTMLElement) => {
  const closest = element.closest<HTMLElement>(autoTagSelector)
  if (!closest) return null
  if (!root.contains(closest)) return null
  return closest
}

const collectTagCandidates = (root: HTMLElement) => {
  const unique: HTMLElement[] = []
  const seen = new Set<HTMLElement>()
  const pushCandidate = (candidate: HTMLElement | null) => {
    if (!candidate || seen.has(candidate)) return
    if (shouldSkipAutoTag(candidate)) return
    seen.add(candidate)
    unique.push(candidate)
  }

  if (root.matches(autoTagSelector)) {
    pushCandidate(resolveTagTarget(root, root))
  }

  root.querySelectorAll<HTMLElement>(autoTagSelector).forEach(element => {
    pushCandidate(resolveTagTarget(element, root))
  })

  return unique
}

const getLocalIndex = (
  target: HTMLElement,
  root: HTMLElement,
  roleKey: string,
  semanticKey: string
) => {
  const candidates = collectTagCandidates(root).filter(candidate => {
    const candidateRoleKey = slugify(getRoleKey(candidate))
    const candidateSemanticKey = slugify(getSemanticKey(candidate))
    return candidateRoleKey === roleKey && candidateSemanticKey === semanticKey
  })
  const index = candidates.findIndex(candidate => candidate === target)
  return index >= 0 ? index + 1 : 1
}

const buildStableAutoId = (element: HTMLElement, root: HTMLElement) => {
  const prefix = getScopePrefix(root)
  const routeKey = slugify(getRouteKey())
  const roleKey = slugify(getRoleKey(element)) || 'unknown-role'
  const semanticKey = slugify(getSemanticKey(element)) || 'element'
  const localIndex = getLocalIndex(element, root, roleKey, semanticKey)
  return `${prefix}-auto-${routeKey}-${roleKey}-${semanticKey}-${localIndex}`.slice(0, 180)
}

const registerAliasForAutoIdChange = (previousId: string, nextId: string) => {
  if (previousId === nextId) return
  registerTutorialIdAlias({
    sourceId: previousId,
    resolvedId: nextId,
    scope: route.path
  })
  if (route.name) {
    registerTutorialIdAlias({
      sourceId: previousId,
      resolvedId: nextId,
      scope: String(route.name)
    })
  }
}

const tagElement = (element: HTMLElement, root: HTMLElement) => {
  const target = resolveTagTarget(element, root)
  if (!target) return
  if (shouldSkipAutoTag(target)) return
  const existingId = target.dataset.tutorialId?.trim()
  const existingLabel = target.dataset.tutorialLabel
  const isAutoSource = target.dataset.tutorialAutoSource === 'auto'
  const isManualId = Boolean(existingId && !isAutoSource)

  if (!isManualId) {
    const nextAutoId = buildStableAutoId(target, root)
    const previousAutoId =
      autoIdHistory.get(target) ?? (isAutoSource && existingId ? existingId : null)

    if (previousAutoId && previousAutoId !== nextAutoId) {
      registerAliasForAutoIdChange(previousAutoId, nextAutoId)
    }

    target.dataset.tutorialId = nextAutoId
    target.dataset.tutorialAutoSource = 'auto'
    autoIdHistory.set(target, nextAutoId)
  }

  if (!existingLabel) {
    target.dataset.tutorialLabel = getAutoLabel(target)
  }
  if (!target.dataset.tutorialRole) {
    target.dataset.tutorialRole = target.getAttribute('role') ?? target.tagName.toLowerCase()
  }
  taggedElements.add(target)
}

const tagTree = (root: HTMLElement) => {
  collectTagCandidates(root).forEach(element => tagElement(element, root))
}

const scheduleAutoTag = (elements: HTMLElement[]) => {
  if (!elements.length) return
  pendingElements.push(...elements)
  if (pending) return
  pending = true
  const run = () => {
    pending = false
    const batch = pendingElements.splice(0)
    batch.forEach(element => {
      if (!props.root) return
      if (!props.root.contains(element)) return
      tagTree(element)
    })
  }
  const requestIdleCallback = (
    window as Window & { requestIdleCallback?: (cb: () => void) => number }
  ).requestIdleCallback
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run)
    return
  }
  window.setTimeout(run, 0)
}

const setupObserver = (root: HTMLElement) => {
  tagTree(root)
  observer = new MutationObserver(mutations => {
    const addedElements: HTMLElement[] = []
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node instanceof HTMLElement) {
          addedElements.push(node)
        }
      })
    })
    scheduleAutoTag(addedElements)
  })
  observer.observe(root, { childList: true, subtree: true })
}

const teardownObserver = () => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

onMounted(() => {
  if (props.root) {
    setupObserver(props.root)
  }
})

watch(
  () => props.root,
  (next, prev) => {
    if (prev && prev !== next) {
      teardownObserver()
    }
    if (next) {
      setupObserver(next)
    }
  }
)

watch(
  () => route.fullPath,
  () => {
    if (!props.root) return
    tagTree(props.root)
  }
)

onBeforeUnmount(() => {
  teardownObserver()
})
</script>

<template>
  <span class="hidden" aria-hidden="true"></span>
</template>
